import { useState, useEffect, useRef } from "react";
import { preVettingApi } from "@/lib/api/preVetting";
import type { FileUploadState, SelectedFile } from "@/components/ui/FileUpload";
import { reviewApi } from "@/lib/api/review";

/**
 * Resolves a private S3 key to a short-lived presigned URL and tracks the
 * fetch lifecycle via a `FileUploadState`.
 *
 * Used in two contexts:
 *  1. **Draft rehydration** — when a pre-vetting draft already contains a
 *     stored key, this hook fetches a signed URL so the `FileUpload` component
 *     can display the success state on mount.
 *  2. **File preview** — `FilePreview` calls this hook to resolve a key before
 *     rendering an image viewer, PDF viewer, or unsupported-type placeholder.
 *
 * State transitions:
 *  - `"idle"`    — no key supplied, or fetch not yet started.
 *  - `"success"` — URL resolved; `accessUrl` and `file` are populated.
 *  - `"error"`   — the document-access endpoint rejected the request.
 *
 * @param savedKey - The S3 key to resolve (e.g. `"pre-vetting/documents/<id>/<uuid>.pdf"`).
 *                   Pass `null | undefined` to skip the fetch and stay in `"idle"`.
 * @returns `{ file, uploadState, accessUrl }`.
 */
export function useRestoredDocumentFile(
  savedKey: string | null | undefined,
  isVendor: boolean = true,
  applicationId?: string
): {
  file: SelectedFile | null;
  uploadState: FileUploadState;
  accessUrl: string | null;
} {
  const [file, setFile] = useState<SelectedFile | null>(null);
  const [uploadState, setUploadState] = useState<FileUploadState>("idle");
  const [accessUrl, setAccessUrl] = useState<string | null>(null);
  // Track the last key we fetched so we don't re-fetch on every parent render.
  const fetchedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    // If the saved key is cleared, reset to the documented "idle" state and
    // clear the last-fetched key so future keys can be fetched again.
    if (!savedKey) {
      fetchedKeyRef.current = null;
      setFile(null);
      setAccessUrl(null);
      setUploadState("idle");
      return;
    }

    // Avoid re-fetching if we've already resolved this key.
    if (fetchedKeyRef.current === savedKey) return;

    // Can't fetch without an applicationId in reviewer mode.
    if (!isVendor && !applicationId) {
      setUploadState("error");
      return;
    }

    fetchedKeyRef.current = savedKey;

    let cancelled = false;

    // Transition to a loading state and clear stale data before fetching.
    setUploadState("uploading");
    setFile(null);
    setAccessUrl(null);

    (async () => {
      try {
        const data = await (!isVendor && !!applicationId
          ? reviewApi.getDocumentAccessUrl(applicationId, savedKey)
          : preVettingApi.getDocumentAccessUrl(savedKey));

        if (cancelled) return;

        // Derive a display name from the key path (last path segment).
        const fileName = savedKey.split("/").pop() ?? savedKey;
        // Guess a MIME type from the extension; default to PDF.
        const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
        const mimeMap: Record<string, string> = {
          pdf: "application/pdf",
          png: "image/png",
          jpg: "image/jpeg",
          jpeg: "image/jpeg",
        };
        const mimeType = mimeMap[ext] ?? "application/pdf";

        setFile({ uri: data.access_url, name: fileName, type: mimeType });
        setAccessUrl(data.access_url);
        setUploadState("success");
      } catch {
        if (!cancelled) {
          // Clear the ref so the same key can be retried after a transient error.
          fetchedKeyRef.current = null;
          setUploadState("error");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [savedKey, isVendor, applicationId]);

  return { file, uploadState, accessUrl };
}
